import React, { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { useAppSelector } from '../../../state/store';
import {
  getActiveMarker,
  getCurrentLocation,
  getLocationsById,
  getSensorsById,
  getSensorsByLocId,
} from '../../../state/selectors';
import { getLocationNameTree } from '../../../utils/locations';
import useGlobalStyles from '../../../styles';
import SensorListItem from './SensorListItem';

const sensorDisplayLimit = 250; // as we might not to load all sensors, specially from root location

interface SensorsInSubLocProps {
  taggedSensors: string[];
  handleTagging: (value: string) => void;
  showAllSensors: boolean;
}

function SensorsInSubLoc({
  taggedSensors,
  handleTagging,
  showAllSensors,
}: SensorsInSubLocProps): JSX.Element {
  const globalClasses = useGlobalStyles();
  const locationsById = useAppSelector(getLocationsById);
  const sensorsById = useAppSelector(getSensorsById);
  const currentLocation = useAppSelector(getCurrentLocation);
  const sensorsByLocId = useAppSelector(getSensorsByLocId);
  const activeMarker = useAppSelector(getActiveMarker);

  const locSensorsRecord = useMemo(() => {
    const sensorRecord = new Map<string, string[]>();
    const allSubLocations = Array.from(locationsById.keys())
      .sort()
      .filter((item) => (currentLocation === '#' ? true : item.startsWith(`${currentLocation}#`)));

    let totalCount = Array.from(sensorRecord.values()).flat(1).length;

    allSubLocations.forEach((location) => {
      if (totalCount < sensorDisplayLimit) {
        const allLocSensors = sensorsByLocId.get(location) ?? [];
        // filter sensors that are already selected for grouping
        const filteredSensors =
          allLocSensors.filter((sensor) => !taggedSensors.includes(sensor)) ?? [];

        // show all sensors or active marker sensors based on selection
        let sensorsList = filteredSensors;
        if (!showAllSensors) {
          sensorsList = filteredSensors.filter((sensor) =>
            sensorsById.get(sensor)?.data?.some((datum) => datum.varName === activeMarker)
          );
        }

        if (sensorsList.length > 0) {
          sensorRecord.set(location, sensorsList);
          totalCount += sensorsList.length;
        }
      }
    });

    return sensorRecord;
  }, [
    activeMarker,
    currentLocation,
    taggedSensors,
    locationsById,
    sensorsById,
    sensorsByLocId,
    showAllSensors,
  ]);

  const showLocationBreadCrumb = (locId: string) => {
    const breadCrumb = getLocationNameTree(locId, locationsById);
    const locIndex = breadCrumb.findIndex((item) => item.id === currentLocation);
    const filteredTree = locIndex !== -1 && breadCrumb.slice(locIndex + 1, breadCrumb.length);
    return (
      <Typography variant="h6" style={{ paddingLeft: '5px', display: 'flex' }}>
        {filteredTree &&
          filteredTree.map((subLocation, i) => (
            <span key={subLocation.id}>
              <span className={filteredTree.length > 2 ? globalClasses.ellipsisLocName : ''}>
                {locationsById.get(subLocation.id)?.name ?? ''}
              </span>
              <span
                className={filteredTree.length > 2 ? globalClasses.ellipsisLocName : ''}
                style={{ padding: '0 5px' }}
              >
                {i !== filteredTree.length - 1 && '-'}
              </span>
            </span>
          ))}
      </Typography>
    );
  };

  const numberOfSensors = Array.from(locSensorsRecord.values()).flat(1).length;

  return (
    <Box sx={{ marginTop: '10px' }}>
      {locSensorsRecord.size > 0 && (
        <>
          <Typography variant="h6">Sensors In Sub Location (s)</Typography>
          <Box sx={{ marginTop: '10px' }}>
            {Array.from(locSensorsRecord.keys()).map((location) => {
              const locationSensors = locSensorsRecord.get(location) ?? [];

              const sortedLocSensors = locationSensors.sort((a, b) => {
                const nameA = sensorsById.get(a)?.name || a;
                const nameB = sensorsById.get(b)?.name || b;
                return nameA.localeCompare(nameB);
              });
              return (
                <>
                  {location && showLocationBreadCrumb(location)}
                  <List>
                    {sortedLocSensors.map((sensor) => (
                      <SensorListItem sensorId={sensor} handleTagging={handleTagging} />
                    ))}
                  </List>
                  <hr />
                </>
              );
            })}
          </Box>

          {numberOfSensors === 0 && (
            <Alert severity="error">No sensors available in Sub location</Alert>
          )}
        </>
      )}
    </Box>
  );
}

export default SensorsInSubLoc;
